import {
  CSSProperties, FC, PropsWithChildren, ReactNode, useCallback, useRef, useState,
} from 'react';
import { useSelect } from 'downshift';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import Icon, { IconIds, Sizes } from 'src/common/components/IconDS22';
import useIsomorphicLayoutEffect from 'src/common/hooks/useIsomorphicLayoutEffect';
import { withSuffix } from 'src/common/utils/dataAttributeHelpers';
import useDropdownPortal from 'src/common/components/DropdownDS22/useDropdownPortal';
import styles from './Dropdown.module.scss';

// The margin between the dropdown and the button
const VERTICAL_MARGIN = 6;

export type DropdownOption<T extends string = string> = {
  /**
   * A unique ID for the option.
   */
  id: T;
  /**
   * The label that displays when the option is selected.
   * If left out, it will default to dropdownLabel
   */
  selectedLabel?: string;
  /**
   * The label that displays in the dropdown.
   * Not required if using `optionRenderer`
   */
  dropdownLabel?: string;
  /**
   * Used with `optionRenderer`. Passed in for each option
   */
  data?: any;
  /**
   * Disable dropdown option
   */
  disabled?: boolean;
  /**
   * icon for the dropdown option
   */
  icon?: IconIds;
};

export const variants = ['default', 'compact', 'white', 'whiteCompact', 'whiteMinimal', 'overlap', 'topNav', 'minimal'] as const;
export type Variant = typeof variants[number];

export const modes = ['select', 'menu'] as const;
type Mode = typeof modes[number];

export const dropdownPositions = ['left', 'right', 'above'] as const;
export type DropdownPosition = typeof dropdownPositions[number];

export const sizes = [
  's',
  'm',
  'l',
] as const;
type Size = typeof sizes[number];

export type DropdownProps = {
  /**
   * A unique id that is used to identify the toggle button. Has a default of `dropdown` which
   * will generate the id of `dropdown-toggle-button`
   */
  id?: string;
  /**
   * An array of DropdownOptions that is set as the options in the Dropdown
   */
  options: DropdownOption[];
  /**
   * The label that describes the dropdown.
   * This will be hidden for users, but readable by screenreaders.
   */
  label?: string;
  /**
   * The default option that should be set if no option is
   */
  defaultOption?: DropdownOption;
  /**
   * Selected value that needs to be set from the options.
   * If this value inits as undefined, then later changes to defined,
   * Downshift interprets this as beginning as uncontrolled and later changing to controlled.
   * And error logs "A component has changed the uncontrolled prop "selectedItem" to be controlled".
   * To avoid this, fallback to `null`, not undefined. To inform downshift of your intent to control
   */
  selectedValue?: DropdownOption | null;
  /**
   * Text that displays when no option is selected, and no defaultOption is set
   */
  instructionLabel?: ReactNode;
  /**
   * Handler function that gets called when a new option is selected
   * @param option The DropdownOption that is set as the currentOption
   */
  onChange: (option: DropdownOption) => void;
  /**
   * A custom render function that will be used to render the selectedItem in the Dropdown button
   * @param option The DropdownOption that will be rendered
   * @returns A ReactNode
   */
  selectedItemRenderer?: (option: DropdownOption) => React.ReactNode;
  /**
   * The mode of the component. By default, it acts like a <select>, but can be switched to
   * menu mode, which disallows selection of items. This allows you to select the same item
   * multiple times.
   */
  mode?: Mode;
  /**
   * The style variant. By default, the button has a gray background.
   * The white variant has a white background.
   */
  variant?: Variant;
  /**
   * The alignment position of the dropdown, relative to the button.
   */
  dropdownPosition?: DropdownPosition;
  /**
   * Remove the max-width so that the dropdown button and container is the full width
   * of the parent container
   */
  fullWidth?: boolean;

  /**
   * Remove padding from dropdown button and selected items
   */
  noPadding?: boolean;
  /**
   * Disable the root dropdown button (not dropdown list items)
   */
  disabled?: HTMLButtonElement['disabled'];
  /**
   * Set the background color of the root dropdown button when disabled.
   */
  disabledBackground?: 'pearl' | 'grey';
  /**
   * When true, this will display the dropdown in an error state.
   */
  error?: boolean;
  /**
   * Limit the max height of the dropdown menu to a certain number of items.
   * for desktop max-height is 450px, for mobile max-height is 350px.
   */
  limitMenuHeight?: boolean;
  /**
   * Data attribute for testing purposes
   */
  'data-test'?: string;
  displayCaret?: boolean;
  /**
   * Size for icon used in dropdown option
   */
  iconSize?: Sizes;
  /**
   * Position of the icon in dropdown option
   */
  iconPosition?: 'left' | 'right';
  /**
   * Adjust the font-size of the dropdown button
   */
  buttonSize?: Size;
  /**
   * Adjust the font-size of the dropdown options
   */
  optionSize?: Size;
  /**
   * A custom render function for the items passed as options.
   * @param option The DropdownOption that will be rendered
   * @returns A ReactNode
   */
  optionRenderer?: (option: DropdownOption) => React.ReactNode;
  selectedIconId?: IconIds;
  selectedIconSize?: Sizes;
  /**
   * The dropdown options use a fixed-position portal (displayed over all content).
   * Show inline if false.
   */
  renderInPortal?: boolean;
  /**
   * Enable text truncation for the dropdown button label and options.
   */
  truncateText?: boolean;
  /**
   * Enable text wrapping for the dropdown options. This will override the `truncateText` option.
   */
  wrapText?: boolean;
};

const itemToString = (item: any) => (typeof (item) === 'string' ? item : '');

// When not using a portal
const SimpleWrapper: FC<PropsWithChildren> = ({ children }) => (
  children
);

const Dropdown: FC<DropdownProps> = ({
  id = 'dropdown',
  options,
  defaultOption,
  selectedValue,
  onChange,
  selectedItemRenderer,
  optionRenderer,
  label,
  instructionLabel = 'Please select...',
  mode = 'select',
  variant = 'default',
  dropdownPosition = 'left',
  fullWidth = false,
  noPadding = false,
  limitMenuHeight = false,
  disabled,
  disabledBackground = 'pearl',
  error,
  'data-test': dataTest,
  displayCaret,
  iconSize = 'm',
  iconPosition = 'left',
  buttonSize,
  optionSize,
  selectedIconId,
  selectedIconSize,
  renderInPortal: renderInPortalPreferred,
  truncateText = false,
  wrapText = false,
}) => {
  const handleSelectedItemChange = useCallback((item: DropdownOption | null | undefined) => {
    if (!item) return;
    onChange(item);
  }, [onChange]);

  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const [openDirection, setOpenDirection] = useState<'up' | 'down'>('down');

  const isOverlap = variant === 'overlap';
  const showCaretVariants = ['default', 'white', 'whiteMinimal', 'compact', 'overlap'];
  const showCaret = displayCaret || showCaretVariants.includes(variant);

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
  } = useSelect({
    id,
    items: options,
    itemToString,
    defaultSelectedItem: defaultOption,
    selectedItem: selectedValue ?? null,
    onSelectedItemChange: ({ selectedItem: newItem }) => handleSelectedItemChange(newItem),
    // Menu mode allows re-selection of the same item, but useSelect won't trigger a change
    // since it will be set to the last selected item. Reset the selectedItem to discard
    // the state and allow re-selection to trigger a change
    ...(mode === 'menu' ? { selectedItem: null } : {}),
  });

  const renderSelectedItem = useCallback(() => {
    if (mode === 'menu') {
      return instructionLabel;
    }
    if (selectedItemRenderer) {
      return selectedItemRenderer(selectedItem || defaultOption);
    }
    if (selectedItem) {
      const item = options.find((option) => selectedItem.id === option.id);
      if (item) {
        return item?.selectedLabel || item?.dropdownLabel;
      }
    }
    return instructionLabel;
  }, [defaultOption, instructionLabel, mode, options, selectedItem, selectedItemRenderer]);

  const { updatePortalDimensions, portalDimensions, portalWrapper } = useDropdownPortal({
    rootRef,
    dropdownRef,
    isEnabled: !!renderInPortalPreferred,
    isOpen,
  });
  const Wrapper = portalWrapper ?? SimpleWrapper;

  useIsomorphicLayoutEffect(() => {
    if (isOpen && !renderInPortalPreferred) {
      if (!rootRef?.current || !dropdownRef?.current) return;

      const rect = rootRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight + VERTICAL_MARGIN;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const hasEnoughSpaceBelow = spaceBelow >= dropdownHeight;
      const hasEnoughSpaceAbove = spaceAbove >= dropdownHeight;
      setOpenDirection(!hasEnoughSpaceBelow && hasEnoughSpaceAbove ? 'up' : 'down');
      return;
    }
    // portal
    if (isOpen) {
      updatePortalDimensions();
    }
  }, [isOpen, options.length]);

  return (
    <div ref={rootRef} className={getClassNames(styles, 'root', classNames({ fullWidth }))}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      {label && (
        <label {...getLabelProps()} className={styles.label}>
          {label}
        </label>
      )}
      <button
        disabled={disabled}
        aria-disabled={disabled}
        type="button"
        className={
          getClassNames(styles, 'button', classNames(
            variant,
            fullWidth && 'fullWidth',
            buttonSize && `body-${buttonSize}`,
            disabled && `disabledBackground_${disabledBackground}`,
            { error, noPadding },
          ))
}
        {...getToggleButtonProps()}
        data-test={withSuffix(dataTest, 'dropdown')}
      >
        {selectedIconId && (
          <span className={getClassNames(styles, 'selectedIcon', classNames({ error }))}>
            <Icon id={selectedIconId} size={selectedIconSize} />
          </span>
        )}
        <span className={getClassNames(
          styles,
          'buttonLabel',
          classNames(variant, {
            error,
            isPlaceholder: mode === 'select'
            && selectedItemRenderer === undefined
            && (selectedItem?.id === undefined || selectedItem?.id === null),
            truncateText,
            wrapText,
          }),
        )}
        >
          {renderSelectedItem()}
        </span>
        {showCaret
          && (
            <span className={getClassNames(styles, 'buttonCaret', classNames({ error }))}>
              <Icon id={isOpen ? 'caret-up' : 'caret-down'} size={isOverlap ? 'm' : 's'} />
            </span>
          )}
      </button>
      <Wrapper>
        <ul
          className={getClassNames(
            styles,
            'dropdown',
            classNames(variant, dropdownPosition, {
              show: isOpen,
              up: openDirection === 'up',
              fullWidth,
              limitMenuHeight,
              portal: !!renderInPortalPreferred,
            }),
          )}
          style={{
            '--top': portalDimensions?.top,
            '--left': portalDimensions?.left,
            '--height': portalDimensions?.height,
            '--width': portalDimensions?.width,
          } as CSSProperties}
          // Ref error due to being rendered in portal. Does not break. Suggested conditional
          // rendering in https://github.com/downshift-js/downshift/issues/1272 still
          // triggers error
          {...getMenuProps({ ref: dropdownRef }, { suppressRefError: !!renderInPortalPreferred })}
        >
          {options.map((item, index) => (
            <li
              data-test="list-option"
              className={getClassNames(
                styles,
                'dropdownItem',
                classNames(variant, optionSize && `body-${optionSize}`, {
                  highlighted: highlightedIndex === index,
                  selected: mode === 'select' && selectedItem?.id === item.id,
                  disabled: item.disabled,
                  truncateText,
                  wrapText,
                  noPadding,
                }),
              )}
              key={`${item.id}${item.selectedLabel}`}
              {...getItemProps({
                item,
                index,
                disabled: item.disabled,
              })}
            >
              {optionRenderer
                ? optionRenderer(item)
                : (
                  <>
                    {isOverlap && selectedItem === item && (
                      <Icon id="check" className={styles.checkIcon} />)}
                    {item?.icon && iconPosition === 'left' && <Icon id={item.icon} size={iconSize} className={styles.icon} />}
                    {item.dropdownLabel}
                    {item?.icon && iconPosition === 'right' && <Icon id={item.icon} size={iconSize} className={styles.icon} />}
                  </>
                )}
            </li>
          ))}
        </ul>
      </Wrapper>
    </div>
  );
};
export default Dropdown;
